'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { aiUsageApi, DeviceRow } from '../_lib/api';

export default function AdminDevicesPage() {
  const { t } = useTranslation();
  const tt = t.aiUsage;
  const [status, setStatus] = useState<'all' | 'active' | 'blocked'>('all');
  const [items, setItems] = useState<DeviceRow[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await aiUsageApi.listDevices({ status, page: 1, pageSize: 50 });
      setItems(res.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [status]);

  const block = async (id: string) => {
    const reason = prompt(tt.device.blockReasonPlaceholder);
    if (!reason) return;
    await aiUsageApi.blockDevice(id, reason);
    await reload();
  };
  const unblock = async (id: string) => {
    await aiUsageApi.unblockDevice(id);
    await reload();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6">{tt.device.listTitle}</h1>

      <div className="flex gap-2 mb-4 text-sm">
        {(['all', 'active', 'blocked'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 py-1 rounded ${status === s ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
          >
            {tt.device[`filter${s.charAt(0).toUpperCase() + s.slice(1)}` as 'filterAll' | 'filterActive' | 'filterBlocked']}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <div className="bg-white rounded shadow">
          <table className="w-full text-sm">
            <thead className="text-gray-500 border-b">
              <tr>
                <th className="py-3 px-4 text-left">{tt.device.columnHostname}</th>
                <th className="py-3 px-4 text-left">{tt.device.columnUser}</th>
                <th className="py-3 px-4 text-left">{tt.device.columnPlatform}</th>
                <th className="py-3 px-4 text-left">{tt.device.columnLastSeen}</th>
                <th className="py-3 px-4 text-left">{tt.device.columnStatus}</th>
                <th className="py-3 px-4 text-right">{tt.device.columnActions}</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-gray-400">No devices</td></tr>
              )}
              {items.map((d) => (
                <tr key={d.id} className="border-b">
                  <td className="py-3 px-4 font-mono text-xs">{d.hostname}</td>
                  <td className="py-3 px-4">
                    {d.user ? (
                      <div>
                        <div className="text-sm">{d.user.displayName}</div>
                        <div className="text-xs text-gray-500">{d.user.email}</div>
                      </div>
                    ) : '-'}
                  </td>
                  <td className="py-3 px-4 text-xs">{d.osPlatform.toLowerCase()}</td>
                  <td className="py-3 px-4 text-xs text-gray-500">
                    {new Date(d.lastSeenAt).toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    {d.blockedAt ? (
                      <span className="text-red-600 text-xs">{tt.device.statusBlocked}</span>
                    ) : (
                      <span className="text-green-600 text-xs">{tt.device.statusActive}</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {d.blockedAt ? (
                      <button onClick={() => unblock(d.id)} className="text-xs text-blue-600 hover:underline">
                        {tt.device.actionUnblock}
                      </button>
                    ) : (
                      <button onClick={() => block(d.id)} className="text-xs text-red-600 hover:underline">
                        {tt.device.actionBlock}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
